import * as React from "react";
import { constNull, pipe } from "fp-ts/function";
import { array, nonEmptyArray, option } from "fp-ts";
import { Button, Dialog } from "@mui/material";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { useOptionalState } from "../../../utils/useOptionalState";
import { CancelableDialogBody } from "../../user/CancelableDialogBody";
import dynamic from "next/dynamic";
import { messages } from "../../../utils/i18n/i18n";
import { useTranslation } from "../../../utils/i18n/lang";
import { SegmentMapInfo } from "../../common/SegmentListMap";

const SegmentListMapNoSSR = dynamic(
    () => import("../../common/SegmentListMap"),
    { ssr: false }
);

interface Props {
    segments: Array<SegmentMapInfo>;
    state: SegmentsToPreviewState;
}
export const ShowSegmentsOnMapBtn = (p: Props) => {
    const m = useTranslation(msg);
    const segmentDetailListOpt = React.useMemo(
        () =>
            pipe(
                p.segments,
                array.filter((s) => s.geojson != null),
                nonEmptyArray.fromArray
            ),
        [p.segments]
    );

    return pipe(
        segmentDetailListOpt,
        option.fold(constNull, (geoJsonList) => (
            <Button
                onClick={() => {
                    p.state.set(geoJsonList);
                }}
            >
                {m.showSegmentsBtn}
            </Button>
        ))
    );
};

export const useSegmentsToPreviewState = () =>
    useOptionalState<NonEmptyArray<SegmentMapInfo>>();

type SegmentsToPreviewState = ReturnType<typeof useSegmentsToPreviewState>;

interface SegmentsMapDialogProps {
    state: SegmentsToPreviewState;
}
export const SegmentsMapDialog = (p: SegmentsMapDialogProps) => {
    const onDialogClose = p.state.clear;
    const m = useTranslation(msg);

    return (
        <Dialog
            open={option.isSome(p.state.value)}
            onClose={onDialogClose}
            keepMounted // map init is expensive
            fullWidth
            maxWidth={"lg"}
        >
            <CancelableDialogBody
                onCancel={onDialogClose}
                cancelBtnLabel={m.closeMapBtn}
            >
                <SegmentListMapNoSSR
                    sx={{
                        height: "80vh",
                        width: "100%",
                    }}
                    segmentList={option.toUndefined(p.state.value)}
                />
            </CancelableDialogBody>
        </Dialog>
    );
};

const msg = messages(
    {
        showSegmentsBtn: "show on map",
        closeMapBtn: "close",
    },
    {
        cs: {
            showSegmentsBtn: "zobraz na mapě",
            closeMapBtn: "zavřít",
        },
    }
);
